// The calendar (SPEC §11, §15; key P, route #/calendar): a full-screen sheet with a black header
// (serif period, SEMAINE / MOIS, previous / next / AUJOURD'HUI, keyboard hints), a toolbar (search,
// floor, department chips, ROUTINES / FAITES toggles, counts), the routines rail and the month grid
// or week columns (an agenda list on phones). Routine runs are dashed, scheduled tasks and finished
// tasks are coloured by department. A day opens the "programmer pour …" popover (task at a time of
// that day, or a routine starting that day with RÉPÉTER). Live: 'task' and 'routine' events.
import { h, button, iconButton, segmented, chip, kbd, badge, setText } from '../dom.js';
import { t } from '../i18n/fr.js';
import { deptColor, formatDateTime, formatNumber } from '../format.js';
import { sortedFloors, floorDepartments, can } from '../store.js';
import * as G from './calendar/grid.js';
import {
  normalizeCalendar, calendarFallback, filterEvents, groupByDay, summarize, filterRoutines, isRelevantTask, monthCapacity,
} from './calendar/events.js';
import { monthView, weekView, agendaView, railView, routineNeedsOk } from './calendar/views.js';
import { createDayPanel } from './calendar/dayform.js';
import { schedulableDepartments } from './calendar/form.js';
import { toList, officeIndex, upsertById, removeById, sortRoutines, routineNextAt, createdTaskId } from './automations/model.js';
import { openRoutineEditor } from './automations/routineeditor.js';
import { childLayers, isMobile, storageGet, storageSet } from './automations/widgets.js';
import './calendar/strings.js';

const VIEW_KEY = 'mo.cal.view';
const RELOAD_DEBOUNCE_MS = 500;
const SEARCH_DEBOUNCE_MS = 150;
const MONTH_HEAD_PX = 27; // weekday header above the month rows (before it is on screen)

/**
 * Default floor scope of the calendar: the floor on screen, or every floor from the building view.
 * @param {object} state app state
 * @returns {string} floor id, '' for every floor
 */
export function defaultFloorScope(state) {
  const floors = state?.office?.floors || [];
  const id = state?.ui?.view === 'building' ? '' : state?.ui?.floorId || '';
  return id && floors.some((f) => f.id === id) ? id : '';
}

/**
 * Departments shown as filter chips for a floor scope ('' = every floor), in building order.
 * @param {object} office
 * @param {string} floorId
 * @returns {Array<object>} departments with `floorName`
 */
export function scopeDepartments(office, floorId) {
  const out = [];
  for (const f of sortedFloors(office?.floors || [])) {
    if (floorId && f.id !== floorId) continue;
    for (const d of floorDepartments(office?.departments || [], f.id)) out.push({ ...d, floorName: f.name });
  }
  return out;
}

/**
 * Opens the calendar sheet.
 * @param {object} ctx app context
 * @returns {import('./overlay.js').Layer}
 */
export function openCalendar(ctx) {
  const now = () => Date.now() + (Number(ctx.store.get().serverOffset) || 0);
  const initialState = ctx.store.get();
  const st = {
    view: storageGet(VIEW_KEY) === 'week' ? 'week' : 'month',
    focus: G.startOfDay(now()), // the selected day (keyboard focus, popover)
    anchor: G.startOfDay(now()), // any day of the period shown
    floorId: defaultFloorScope(initialState),
    deptIds: new Set(initialState.ui?.deptFilter ? [initialState.ui.deptFilter] : []),
    showRoutines: true,
    showDone: true,
    query: '',
    routineFilter: null,
    range: null,
    events: [],
    dataStatus: 'loading',
    dataError: '',
    fallback: false,
    truncated: false,
    routineRuns: null, // {active, paused, capped}: the planned runs of the period before the server's caps
    routines: [],
    routinesStatus: 'loading',
    mobile: isMobile(),
    railOpen: false,
  };
  if (st.deptIds.size && !scopeDepartments(initialState.office, st.floorId).some((d) => st.deptIds.has(d.id))) st.deptIds.clear();

  let layer = null;
  let children = null;
  let dayLayer = null;
  let pendingRender = false;
  let reloadTimer = null;
  let searchTimer = null;
  let minuteTimer = null;
  let monthCap = 0; // event rows per month cell in the grid on screen
  let resizeRaf = 0;
  let loadSeq = 0;
  let routinesSeq = 0;
  let todayKey = G.dayKey(now());
  const offs = [];
  const mark = (el, key) => { el.dataset.fk = key; return el; };

  // --- header (in the black layer head) --------------------------------------------------------------
  const viewSeg = segmented([
    { value: 'week', label: t('calendar.week') },
    { value: 'month', label: t('calendar.month') },
  ], { value: st.view, size: 'sm', label: t('cal.viewLabel'), onChange: (v) => setView(v) });
  viewSeg.classList.add('cal-viewseg');
  const prevBtn = iconButton('chevronLeft', t('cal.prevMonth'), { class: 'cal-nav', onClick: () => shift(-1) });
  const nextBtn = iconButton('chevronRight', t('cal.nextMonth'), { class: 'cal-nav', onClick: () => shift(1) });
  const periodMain = h('span', { class: 'cal-period-main' });
  const periodYear = h('span', { class: 'cal-period-year' });
  const period = h('p', { class: 'cal-period', 'aria-live': 'polite' }, periodMain, ' ', periodYear);
  const todayBtn = button(t('calendar.today'), { variant: 'secondary', size: 'sm', class: 'cal-today-btn', title: t('cal.todayTitle'), onClick: () => goToday() });
  const keyHints = h('p', { class: 'cal-keys', 'aria-hidden': 'true' },
    kbd('←'), kbd('→'), h('span', {}, t('cal.hint.move')), ' · ',
    kbd('W'), kbd('M'), h('span', {}, t('cal.hint.view')), ' · ',
    kbd('T'), h('span', {}, t('cal.hint.today')), ' · ',
    kbd('Esc'), h('span', {}, t('cal.hint.close')));
  const headerActions = h('div', { class: 'cal-headbar' }, viewSeg, h('div', { class: 'cal-periodnav' }, prevBtn, period, nextBtn), todayBtn, keyHints);

  // --- toolbar ---------------------------------------------------------------------------------------
  const searchInput = h('input', {
    type: 'search', class: 'input input-sm cal-search', placeholder: t('cal.search'), 'aria-label': t('cal.searchLabel'),
    autocomplete: 'off', spellcheck: false,
  });
  searchInput.addEventListener('input', () => {
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => { st.query = searchInput.value.trim(); render(); }, SEARCH_DEBOUNCE_MS);
  });
  const floorSelect = h('select', { class: 'input select input-sm cal-floor', 'aria-label': t('cal.floor') });
  floorSelect.addEventListener('change', () => {
    st.floorId = floorSelect.value;
    st.deptIds.clear();
    st.routineFilter = null;
    loadRoutines();
    load();
  });
  const chipsBox = h('div', { class: 'cal-chips', role: 'group', 'aria-label': t('cal.filters') });
  const togglesBox = h('div', { class: 'cal-chips cal-toggles' });
  const summaryBox = h('p', { class: 'cal-summary', 'aria-live': 'polite' });
  const toolbar = h('div', { class: 'cal-toolbar' },
    h('div', { class: 'cal-toolbar-left' }, searchInput, floorSelect),
    chipsBox, togglesBox, summaryBox);

  // --- body ------------------------------------------------------------------------------------------
  const railHost = h('aside', { class: 'cal-rail', 'aria-label': t('cal.rail.title') });
  const notice = h('div', { class: 'cal-notice', hidden: true });
  const gridHost = h('div', { class: 'cal-gridhost' });
  const main = h('section', { class: 'cal-main', 'aria-label': t('calendar.title') }, notice, gridHost);
  const root = h('div', { class: 'cal-root' }, toolbar, h('div', { class: 'cal-body' }, railHost, main));

  // --- helpers -----------------------------------------------------------------------------------------
  const office = () => ctx.store.get().office || {};
  const index = () => officeIndex(office());
  const currentRange = () => G.viewRange(st.view, st.anchor);
  const rangeKey = (r) => `${r.from}-${r.to}-${st.floorId}`;
  const canScheduleHere = () => schedulableDepartments(ctx.store.get(), st.floorId).length > 0;

  function filteredEvents() {
    return filterEvents(st.events, {
      deptIds: st.deptIds, showRoutines: st.showRoutines, showDone: st.showDone, query: st.query,
      routineId: st.routineFilter, office: office(),
    });
  }

  // --- rendering -------------------------------------------------------------------------------------
  function renderHeader() {
    viewSeg.setValue(st.view);
    const p = G.periodTitle(st.view, st.anchor);
    setText(periodMain, p.main);
    setText(periodYear, p.year);
    const week = st.view === 'week';
    prevBtn.setAttribute('aria-label', t(week ? 'cal.prevWeek' : 'cal.prevMonth'));
    prevBtn.setAttribute('title', t(week ? 'cal.prevWeek' : 'cal.prevMonth'));
    nextBtn.setAttribute('aria-label', t(week ? 'cal.nextWeek' : 'cal.nextMonth'));
    nextBtn.setAttribute('title', t(week ? 'cal.nextWeek' : 'cal.nextMonth'));
  }

  function renderToolbar(events) {
    const off = office();
    const floors = sortedFloors(off.floors || []);
    floorSelect.replaceChildren(h('option', { value: '' }, t('cal.floorAll')));
    const uiFloor = ctx.store.get().ui?.floorId;
    for (const f of floors) {
      floorSelect.appendChild(h('option', { value: f.id }, f.id === uiFloor ? t('cal.floorCurrent', { name: f.name }) : f.name));
    }
    floorSelect.value = st.floorId;
    floorSelect.hidden = floors.length < 2;

    const depts = scopeDepartments(off, st.floorId);
    for (const id of [...st.deptIds]) if (!depts.some((d) => d.id === id)) st.deptIds.delete(id);
    chipsBox.replaceChildren(...depts.map((d) => {
      const active = st.deptIds.has(d.id);
      const c = chip(d.name, { active, dot: deptColor(d.color, d.id), onClick: () => toggleDept(d.id) });
      c.setAttribute('title', st.floorId ? t('cal.deptChipAria', { name: d.name }) : `${d.floorName} · ${d.name}`);
      return mark(c, `dept:${d.id}`);
    }));
    chipsBox.hidden = depts.length < 2;

    const routineChip = chip(t('cal.toggleRoutines'), { active: st.showRoutines, onClick: () => { st.showRoutines = !st.showRoutines; render(); } });
    routineChip.setAttribute('aria-label', t('cal.toggleRoutinesAria'));
    const doneChip = chip(t('cal.toggleDone'), { active: st.showDone, onClick: () => { st.showDone = !st.showDone; render(); } });
    doneChip.setAttribute('aria-label', t('cal.toggleDoneAria'));
    const items = [mark(routineChip, 'toggle:routines'), mark(doneChip, 'toggle:done')];
    if (st.routineFilter) {
      const r = st.routines.find((x) => x.id === st.routineFilter);
      const rc = chip(t('cal.routineFilter', { title: r?.title || st.routineFilter }), { active: true, onClick: () => setRoutineFilter(null) });
      rc.classList.add('cal-routine-chip');
      rc.setAttribute('aria-label', t('cal.routineFilterClear'));
      items.push(mark(rc, 'toggle:routine'));
    }
    togglesBox.replaceChildren(...items);

    summaryBox.replaceChildren(...summaryParts(summarize(events)));
  }

  /**
   * « 5 000+ PASSAGES PRÉVUS · 0 PROGRAMMÉE · 1 002 FAITES · 3 EN ERREUR » with a tooltip each and
   * a legend button: planned runs are no tasks; paused routines and idle passes do not count; only
   * the errors nobody dealt with are « en erreur ».
   */
  function summaryParts(s) {
    const capped = !!(st.routineRuns?.capped || st.truncated);
    // Unfiltered, the server's own count of the period (before its caps) is the closest.
    const filtered = st.deptIds.size > 0 || !!st.query || !!st.routineFilter || !st.showRoutines;
    const routines = !filtered && st.routineRuns ? Math.max(s.routines, Number(st.routineRuns.active) || 0) : s.routines;
    const paused = !filtered && st.routineRuns ? Math.max(s.paused, Number(st.routineRuns.paused) || 0) : s.paused;
    const part = (n, key, title, { plus = false, tone = '' } = {}) => h('span', { class: ['cal-sum', tone && `is-${tone}`], title },
      h('b', { class: 'cal-sum-n' }, `${formatNumber(n)}${plus ? '+' : ''}`), ' ', h('span', { class: 'mono-label' }, t(key, { n })));
    const legend = iconButton('info', t('cal.summary.legend'), { class: 'cal-sum-legend', onClick: (e) => openLegend(e.currentTarget) });
    return [
      part(routines, 'cal.summary.routines', t('cal.summary.routinesTitle', { capped: capped ? t('cal.summary.cappedNote') : '' }), { plus: capped }),
      paused ? part(paused, 'cal.summary.paused', t('cal.summary.pausedTitle'), { plus: capped, tone: 'muted' }) : null,
      part(s.scheduled, 'cal.summary.scheduled', t('cal.summary.scheduledTitle')),
      part(s.done, 'cal.summary.done', t('cal.summary.doneTitle')),
      part(s.errors, 'cal.summary.errors', t('cal.summary.errorsTitle'), { tone: s.errors ? 'danger' : '' }),
      legend,
    ].filter(Boolean);
  }

  function openLegend(anchor) {
    const row = (key, text) => h('div', { class: 'dl-row' }, h('dt', { class: 'mono-label' }, t(key, { n: 2 })), h('dd', {}, text));
    const content = h('div', { class: 'cal-legend' },
      h('dl', { class: 'dl' },
        row('cal.summary.routines', t('cal.summary.routinesTitle', { capped: '' })),
        row('cal.summary.paused', t('cal.summary.pausedTitle')),
        row('cal.summary.scheduled', t('cal.summary.scheduledTitle')),
        row('cal.summary.done', t('cal.summary.doneTitle')),
        row('cal.summary.errors', t('cal.summary.errorsTitle'))),
      button(t('cal.summary.openBoard'), { variant: 'link', size: 'sm', iconRight: 'arrowRight', onClick: () => ctx.navigate('#/taches') }));
    children.add(ctx.overlay.popover(anchor, content, { placement: 'bottom-end', label: t('cal.summary.legendTitle'), className: 'cal-legend-pop' }));
  }

  function renderRail() {
    const routines = filterRoutines(sortRoutines(st.routines), { floorId: st.floorId, deptIds: st.deptIds, query: st.query, office: office() });
    const state = ctx.store.get();
    const canManageAny = schedulableDepartments(state, st.floorId).some((d) => can(state, 'manage', { deptId: d.id }));
    const inner = railView({
      routines, total: st.routines.length, routineFilter: st.routineFilter, idx: index(), now: now(), status: st.routinesStatus,
      canManage: (deptId) => can(ctx.store.get(), 'manage', { deptId }),
      onFilter: (id) => setRoutineFilter(st.routineFilter === id ? null : id),
      onEdit: (r) => editRoutine(r.id),
      onManage: canManageAny ? () => ctx.navigate('#/automations/routines') : null,
    });
    if (st.mobile) {
      let details = null;
      details = h('details', { class: 'cal-rail-details', open: st.railOpen, onToggle: () => { st.railOpen = !!details?.open; } },
        h('summary', { class: 'mono-label' }, t('cal.rail.toggle', { n: routines.length })), inner);
      railHost.replaceChildren(details);
    } else {
      railHost.replaceChildren(inner);
    }
  }

  function renderNotice() {
    const lines = [];
    if (st.dataStatus === 'error') {
      lines.push(h('div', { class: 'cal-notice-row is-error', role: 'alert' },
        h('span', {}, st.dataError || t('cal.error')),
        button(t('cal.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => load() })));
    }
    if (st.fallback) lines.push(h('p', { class: 'cal-notice-row' }, t('cal.fallback')));
    if (st.truncated) lines.push(h('p', { class: 'cal-notice-row' }, t('cal.truncated')));
    notice.replaceChildren(...lines);
    notice.hidden = !lines.length;
  }

  function renderGrid(events) {
    const byDay = groupByDay(events);
    const idx = index();
    const canSchedule = canScheduleHere();
    const focusKey = G.dayKey(st.focus);
    const common = { byDay, focusKey, idx, canSchedule, onDay: openDay, onEvent: openEvent };
    let view;
    if (st.mobile) {
      const days = st.view === 'week'
        ? G.weekGrid(st.anchor, { now: now() }).days
        : G.monthGrid(st.anchor, { now: now() }).weeks.flat().filter((d) => d.inMonth);
      view = agendaView({ ...common, days });
    } else if (st.view === 'week') {
      view = weekView({ ...common, week: G.weekGrid(st.anchor, { now: now() }), label: t('cal.gridWeek') });
    } else {
      const grid = G.monthGrid(st.anchor, { now: now() });
      monthCap = monthCapacity(grid.weeks.length, monthRowsHeight());
      view = monthView({ ...common, grid, capacity: monthCap, label: t('cal.grid') });
    }
    gridHost.replaceChildren(view);
  }

  /** Height (px) the month rows can share: the grid area minus the weekday header (0 = unknown). */
  function monthRowsHeight() {
    const hostH = gridHost.clientHeight || 0;
    if (!hostH) return 0;
    const head = gridHost.querySelector?.('.cal-month > [role="rowgroup"]')?.offsetHeight || MONTH_HEAD_PX;
    return hostH - head;
  }

  /** A resize that changes how many events a month cell holds redraws the grid. */
  function onResize() {
    if (resizeRaf) return;
    resizeRaf = (globalThis.requestAnimationFrame || setTimeout)(() => {
      resizeRaf = 0;
      if (!layer?.isOpen() || st.mobile || st.view !== 'month' || dayLayer?.isOpen()) return;
      const grid = G.monthGrid(st.anchor, { now: now() });
      if (monthCapacity(grid.weeks.length, monthRowsHeight()) !== monthCap) renderGrid(filteredEvents());
    });
  }

  function render() {
    if (!layer?.isOpen()) return;
    if (dayLayer?.isOpen()) { pendingRender = true; renderHeader(); return; }
    const active = globalThis.document?.activeElement;
    const fk = active?.dataset?.fk || null;
    const gridFocused = !!(active?.dataset?.day || active?.dataset?.ev);
    const events = filteredEvents();
    renderHeader();
    renderToolbar(events);
    renderRail();
    renderNotice();
    renderGrid(events);
    const again = fk ? [...root.querySelectorAll('[data-fk]')].find((el) => el.dataset.fk === fk) : null;
    if (again) again.focus?.();
    else if (gridFocused) focusCell();
  }

  function cellOf(key) {
    return [...gridHost.querySelectorAll('[data-day]')].find((c) => c.dataset.day === key) || null;
  }

  function focusCell() {
    cellOf(G.dayKey(st.focus))?.focus?.();
  }

  // --- actions -----------------------------------------------------------------------------------------
  function afterFocusChange(focusGrid = true) {
    const key = rangeKey(currentRange());
    if (!st.range || key !== rangeKey(st.range)) load();
    render();
    if (focusGrid) focusCell();
  }

  function setView(v) {
    if (v !== 'week' && v !== 'month') return;
    if (st.view === v) return;
    st.view = v;
    st.anchor = st.focus;
    storageSet(VIEW_KEY, v);
    afterFocusChange(false);
  }

  function shift(delta) {
    st.anchor = G.startOfDay(G.shiftAnchor(st.view, st.anchor, delta));
    st.focus = G.startOfDay(G.shiftAnchor(st.view, st.focus, delta));
    if (!G.inRange(st.focus, currentRange())) st.focus = st.anchor;
    afterFocusChange(false);
  }

  function goToday() {
    st.focus = G.startOfDay(now());
    st.anchor = st.focus;
    afterFocusChange(true);
  }

  function moveFocus(delta) {
    st.focus = G.addDays(st.focus, delta);
    // Leaving the month (month view) or the week (week view) moves the period with the focus.
    const leaves = st.view === 'week'
      ? !G.inRange(st.focus, currentRange())
      : new Date(st.focus).getMonth() !== new Date(st.anchor).getMonth() || new Date(st.focus).getFullYear() !== new Date(st.anchor).getFullYear();
    if (leaves) st.anchor = st.focus;
    afterFocusChange(true);
  }

  function toggleDept(id) {
    if (st.deptIds.has(id)) st.deptIds.delete(id); else st.deptIds.add(id);
    render();
  }

  function setRoutineFilter(id) {
    st.routineFilter = id;
    if (id && !st.showRoutines) st.showRoutines = true;
    render();
  }

  function eventsOfDay(key) {
    return filteredEvents().filter((ev) => ev.day === key);
  }

  function openDay(d, anchorEl) {
    dayLayer?.close('api');
    if (G.dayKey(st.focus) !== d.key) {
      st.focus = d.ms;
      for (const c of gridHost.querySelectorAll('[data-day]')) {
        const on = c.dataset.day === d.key;
        c.tabIndex = on ? 0 : -1;
        c.setAttribute('aria-selected', String(on));
        c.classList.toggle('is-focus', on);
      }
    }
    const panel = createDayPanel(ctx, {
      dayMs: d.ms, floorId: st.floorId, preferredDeptIds: st.deptIds, events: eventsOfDay(d.key), idx: index(), now,
      onEvent: (ev) => { dayLayer?.close('select'); openEvent(ev); },
      onDone: () => { dayLayer?.close('confirm'); load(); },
      onClose: () => dayLayer?.close('button'),
    });
    const connected = anchorEl && anchorEl.isConnected !== false;
    if (st.mobile || !connected) {
      dayLayer = ctx.overlay.openModal({
        title: G.longDayLabel(d.ms), eyebrow: t('cal.form.eyebrow'), size: 'md', className: 'cal-daymodal', content: panel.el,
        initialFocus: panel.initialFocus || 'first',
      });
    } else {
      dayLayer = ctx.overlay.popover(anchorEl, panel.el, {
        placement: 'bottom-start', className: 'cal-pop', label: G.longDayLabel(d.ms), initialFocus: panel.initialFocus || 'first',
      });
    }
    children.add(dayLayer);
    const opened = dayLayer;
    opened.onClose((reason) => {
      if (dayLayer === opened) dayLayer = null;
      if (!pendingRender) return;
      pendingRender = false;
      // A click outside closes the popover on pointerdown: re-render after that click lands, so
      // the day the user clicked is still in the DOM and opens its own popover.
      if (reason === 'outside') setTimeout(render, 350); else render();
    });
  }

  function openEvent(ev) {
    if (ev.taskId) {
      ctx.navigate(`#/task/${encodeURIComponent(ev.taskId)}`);
      return;
    }
    if (ev.routineId) openRoutineDetails(ev);
  }

  async function findRoutine(id) {
    const known = st.routines.find((r) => r.id === id);
    if (known) return known;
    try {
      return await ctx.api.get(`/api/routines/${encodeURIComponent(id)}`);
    } catch (err) {
      ctx.toast.error(err);
      return null;
    }
  }

  async function editRoutine(id) {
    const routine = await findRoutine(id);
    if (!routine || !layer?.isOpen()) return;
    children.add(openRoutineEditor(ctx, {
      routine,
      onSaved: (saved) => { if (saved?.id) st.routines = upsertById(st.routines, saved); render(); scheduleReload(0); },
      onDeleted: (rid) => { st.routines = removeById(st.routines, rid); if (st.routineFilter === rid) st.routineFilter = null; render(); scheduleReload(0); },
    }));
  }

  async function runRoutine(id, btn) {
    btn.disabled = true;
    btn.classList.add('is-busy');
    try {
      const res = await ctx.api.post(`/api/routines/${encodeURIComponent(id)}/run`, {});
      if (res?.id && res.state) ctx.store.applyTask?.(res);
      const taskId = createdTaskId(res);
      ctx.toast.success(t('cal.ro.ran'), taskId ? { action: { label: t('common.open'), onClick: () => ctx.navigate(`#/task/${encodeURIComponent(taskId)}`) } } : {});
      return true;
    } catch (err) {
      ctx.toast.error(err);
      return false;
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-busy');
    }
  }

  function openRoutineDetails(ev) {
    const routine = st.routines.find((r) => r.id === ev.routineId) || null;
    const idx = index();
    const agent = idx.agents.get(ev.agentId);
    const dept = idx.depts.get(ev.deptId);
    const state = ctx.store.get();
    const canManage = can(state, 'manage', { deptId: ev.deptId });
    const canOperate = can(state, 'operate', { deptId: ev.deptId });
    let details = null;
    const row = (label, value) => (value ? h('div', { class: 'dl-row' }, h('dt', {}, label), h('dd', {}, value)) : null);
    const next = routine ? routineNextAt(routine) : null;
    const runBtn = canOperate ? button(t('cal.ro.run'), {
      variant: 'secondary', size: 'sm', icon: 'bolt',
      onClick: async () => { if (await runRoutine(ev.routineId, runBtn)) details?.close('confirm'); },
    }) : null;
    const content = h('div', { class: 'stack cal-ro', style: { '--dept': deptColor(dept?.color, ev.deptId) } },
      h('dl', { class: 'dl' },
        row(t('cal.ro.agent'), agent ? `${agent.name}${agent.role ? ` — ${agent.role}` : ''}` : ''),
        row(t('cal.form.dept'), dept?.name || ''),
        row(t('cal.ro.when'), ev.description || routine?.description || ''),
        row(t('cal.ro.occurrence'), formatDateTime(ev.at, now())),
        row(t('cal.ro.next'), routine?.paused ? t('cal.rail.paused') : next ? formatDateTime(next, now()) : ''),
        row(t('auto.autonomy'), routineNeedsOk(routine || { autonomy: 'inherit' }, agent) ? t('auto.autonomy.approve') : t('auto.autonomy.auto'))),
      ev.paused || routine?.paused ? h('p', {}, badge(t('cal.rail.paused'), 'muted')) : null);
    details = ctx.overlay.openModal({
      title: ev.title || t('cal.ro.title'), eyebrow: t('cal.ro.title').toUpperCase(), size: 'sm', className: 'cal-romodal', content,
      footer: [
        button(t('cal.ro.filter'), { variant: 'ghost', size: 'sm', icon: 'eye', onClick: () => { details.close('select'); setRoutineFilter(ev.routineId); } }),
        h('span', { class: 'spacer' }),
        runBtn,
        canManage ? button(t('cal.ro.edit'), { variant: 'primary', size: 'sm', onClick: () => { details.close('select'); editRoutine(ev.routineId); } }) : null,
      ],
    });
    children.add(details);
  }

  // --- data --------------------------------------------------------------------------------------------
  async function load() {
    const seq = ++loadSeq;
    const range = currentRange();
    const sameRange = st.range && rangeKey(st.range) === rangeKey(range);
    st.range = range;
    if (!sameRange) { st.events = []; st.dataStatus = 'loading'; }
    layer?.setBusy?.(true);
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/calendar', { from: range.from, to: range.to, floorId: st.floorId }));
      if (seq !== loadSeq || !layer?.isOpen()) return;
      st.events = normalizeCalendar(res, { from: range.from, to: range.to, now: now() });
      st.truncated = !!res?.truncated;
      st.routineRuns = res?.routineRuns && typeof res.routineRuns === 'object' ? res.routineRuns : null;
      st.fallback = false;
      st.dataStatus = 'ok';
      st.dataError = '';
    } catch (err) {
      if (seq !== loadSeq || !layer?.isOpen()) return;
      if (err?.status === 404) {
        let routines = st.routines;
        if (st.routinesStatus !== 'ok') {
          try { routines = toList(await ctx.api.get(ctx.api.withQuery('/api/routines', { floorId: st.floorId })), 'routines'); } catch { routines = []; }
          if (seq !== loadSeq || !layer?.isOpen()) return;
        }
        const data = calendarFallback({ routines, tasks: ctx.store.get().tasks?.values?.() || [], from: range.from, to: range.to, floorId: st.floorId });
        st.events = normalizeCalendar(data, { from: range.from, to: range.to, now: now() });
        st.fallback = true;
        st.truncated = false;
        st.routineRuns = null;
        st.dataStatus = 'ok';
      } else {
        st.dataStatus = 'error';
        st.dataError = err?.message || t('cal.error');
      }
    } finally {
      if (seq === loadSeq) layer?.setBusy?.(false);
    }
    render();
  }

  async function loadRoutines() {
    const seq = ++routinesSeq;
    st.routinesStatus = 'loading';
    try {
      const res = await ctx.api.get(ctx.api.withQuery('/api/routines', { floorId: st.floorId }));
      if (seq !== routinesSeq || !layer?.isOpen()) return;
      st.routines = toList(res, 'routines');
      st.routinesStatus = 'ok';
    } catch (err) {
      if (seq !== routinesSeq || !layer?.isOpen()) return;
      st.routines = [];
      st.routinesStatus = err?.status === 404 ? 'unavailable' : 'error';
    }
    render();
  }

  function scheduleReload(delay = RELOAD_DEBOUNCE_MS) {
    clearTimeout(reloadTimer);
    reloadTimer = setTimeout(() => { if (layer?.isOpen()) load(); }, delay);
  }

  // --- open ----------------------------------------------------------------------------------------------
  layer = ctx.overlay.openSheet({
    title: t('calendar.title'), eyebrow: t('cal.eyebrow'), tone: 'dark', className: 'cal-sheet', content: root, actions: headerActions,
  });
  children = childLayers(layer);

  offs.push(ctx.events.on('task', (task) => {
    if (!st.range) return;
    const known = new Set(st.events.filter((e) => e.taskId).map((e) => e.taskId));
    if (isRelevantTask(task, st.range, known)) scheduleReload();
  }));
  offs.push(ctx.events.on('task.removed', (d) => {
    if (d?.id && st.events.some((e) => e.taskId === d.id)) scheduleReload();
  }));
  offs.push(ctx.events.on('routine', (d) => {
    const r = d?.routine && typeof d.routine === 'object' ? d.routine : d;
    if (!r?.id) return;
    if (!st.floorId || r.floorId === st.floorId) st.routines = upsertById(st.routines, r);
    else st.routines = removeById(st.routines, r.id);
    render();
    scheduleReload();
  }));
  offs.push(ctx.events.on('routine.removed', (d) => {
    if (!d?.id) return;
    st.routines = removeById(st.routines, d.id);
    if (st.routineFilter === d.id) st.routineFilter = null;
    render();
    scheduleReload();
  }));
  offs.push(ctx.events.on('calendar', () => scheduleReload()));
  offs.push(ctx.events.on('hello', () => { loadRoutines(); scheduleReload(0); }));
  offs.push(ctx.store.on('office', (off) => {
    if (st.floorId && !(off?.floors || []).some((f) => f.id === st.floorId)) {
      st.floorId = '';
      st.deptIds.clear();
      loadRoutines();
      load();
      return;
    }
    render();
  }));

  const popKeys = ctx.keys.pushScope((e, { editable }) => {
    if (!layer?.isOpen() || ctx.overlay.top?.() !== layer) return false;
    if (editable || e.ctrlKey || e.metaKey || e.altKey) return false;
    const delta = G.arrowDelta(e.key);
    if (delta !== null) { moveFocus(delta); return true; }
    if (e.key === 'PageUp') { shift(-1); return true; }
    if (e.key === 'PageDown') { shift(1); return true; }
    const k = String(e.key || '').toLowerCase();
    if (k === 'w') { setView('week'); return true; }
    if (k === 'm') { setView('month'); return true; }
    if (k === 't') { goToday(); return true; }
    return false;
  });

  globalThis.addEventListener?.('resize', onResize);

  let mq = null;
  const onMq = () => { const m = isMobile(); if (m !== st.mobile) { st.mobile = m; dayLayer?.close('api'); render(); } };
  try {
    mq = globalThis.matchMedia?.('(max-width: 767px)') || null;
    mq?.addEventListener?.('change', onMq);
  } catch { mq = null; }

  minuteTimer = setInterval(() => {
    const key = G.dayKey(now());
    if (key !== todayKey) { todayKey = key; scheduleReload(0); } else if (!dayLayer?.isOpen()) renderRail();
  }, 60_000);

  layer.onClose(() => {
    for (const off of offs) { try { off?.(); } catch { /* ignore */ } }
    popKeys();
    clearTimeout(reloadTimer);
    clearTimeout(searchTimer);
    clearInterval(minuteTimer);
    try { mq?.removeEventListener?.('change', onMq); } catch { /* ignore */ }
    globalThis.removeEventListener?.('resize', onResize);
    if (resizeRaf) globalThis.cancelAnimationFrame?.(resizeRaf);
    loadSeq += 1;
    routinesSeq += 1;
  });

  st.range = null;
  render();
  loadRoutines();
  load();
  return layer;
}
